import React, { useState, useEffect } from 'react';
import { X, FileText, CheckCircle, AlertCircle, Clock, Zap, TrendingUp, Target, Search, Map, Download, AlertTriangle } from 'lucide-react';
import { Contract } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { IntelligentProposalGenerator } from '../lib/intelligent-proposal-integration';
import toast from 'react-hot-toast';

interface EnhancedProposalGeneratorProps {
  contract: Contract;
  onClose: () => void;
  onSuccess: () => void;
}

interface StepStatus {
  id: string;
  label: string;
  status: 'pending' | 'active' | 'complete' | 'error';
  description: string;
}

interface AnalysisData {
  intelligenceData?: any;
  rfpAnalysis?: any;
  gapAnalysis?: any;
  proposal?: any;
  feedback?: any;
  improvementRoadmap?: any;
}

const ENHANCED_GENERATION_STEPS: StepStatus[] = [
  {
    id: 'intelligence-extraction',
    label: 'Intelligence Extraction',
    status: 'pending',
    description: 'Extracting comprehensive intelligence from profile and documents'
  },
  {
    id: 'rfp-analysis',
    label: 'RFP Analysis',
    status: 'pending',
    description: 'Analyzing contract requirements and evaluation criteria'
  },
  {
    id: 'gap-analysis',
    label: 'Gap Analysis',
    status: 'pending',
    description: 'Identifying capability gaps and improvement opportunities'
  },
  {
    id: 'deliverables-generation',
    label: 'Deliverables Analysis',
    status: 'pending',
    description: 'Extracting deliverables and generating labor framework'
  },
  {
    id: 'proposal-generation',
    label: 'Proposal Generation',
    status: 'pending',
    description: 'Generating comprehensive proposal with pricing'
  },
  {
    id: 'finalization',
    label: 'Finalization',
    status: 'pending',
    description: 'Storing enhanced proposal and generating summary'
  }
];

export function EnhancedProposalGenerator({ contract, onClose, onSuccess }: EnhancedProposalGeneratorProps) {
  const { profile } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('intelligence-extraction');
  const [steps, setSteps] = useState<StepStatus[]>(ENHANCED_GENERATION_STEPS);
  const [error, setError] = useState<string | null>(null);
  const [analysisData, setAnalysisData] = useState<AnalysisData>({});
  const [proposalContent, setProposalContent] = useState<any>(null);
  const [existingProposal, setExistingProposal] = useState<any>(null);
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [hasStartedGeneration, setHasStartedGeneration] = useState(false);

  useEffect(() => {
    // Check for existing proposal first, then auto-start generation
    if (!hasStartedGeneration) {
      checkExistingProposal();
    }
  }, [hasStartedGeneration]);

  // Handle cancellation
  const handleCancel = () => {
    if (generating) {
      setCancelling(true);
      setGenerating(false);
      setError('Generation cancelled by user');
      setSteps(ENHANCED_GENERATION_STEPS.map(step => ({ 
        ...step, 
        status: step.status === 'active' ? 'error' : step.status 
      })));
      toast.error('Proposal generation cancelled');
      setTimeout(() => {
        onClose();
      }, 1000);
    } else {
      onClose();
    }
  };

  // 🎯 ENHANCED: Generate formal cover letter with proper addressing
  const generateFormalCoverLetter = (proposalData: any, contractData: any, profileData: any) => {
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Extract contact information from RFP analysis or contract
    const contractingOfficer = proposalData.rfpAnalysis?.contractingOfficer || 
                              proposalData.rfpAnalysis?.pointOfContact || 
                              contractData.pointOfContact;
    
    const agencyAddress = contractData.agency_address || 
                         proposalData.rfpAnalysis?.agencyAddress || 
                         contractData.placeOfPerformance;

    // Company information
    const companyName = profileData.company_name || 'Professional Services Company';
    const companyAddress = profileData.company_address || profileData.business_address;
    const companyPhone = profileData.phone || profileData.company_phone;
    const companyEmail = profileData.email || profileData.company_email;

    return `${companyName}
${companyAddress || '[Company Address]'}
${companyPhone ? `Phone: ${companyPhone}` : ''}
${companyEmail ? `Email: ${companyEmail}` : ''}

${currentDate}

${contractingOfficer?.name || 'Contracting Officer'}
${contractingOfficer?.title || 'Contracting Officer'}
${contractData.agency || 'Government Agency'}
${agencyAddress?.street || '[Agency Address]'}
${agencyAddress?.city || ''} ${agencyAddress?.state || ''} ${agencyAddress?.zip || ''}

RE: ${contractData.title || 'Contract Proposal'}
    Solicitation Number: ${contractData.solicitationNumber || contractData.notice_id || 'N/A'}
    NAICS Code: ${contractData.naicsCode || 'N/A'}

Dear ${contractingOfficer?.name ? `${contractingOfficer.name.split(' ')[0]} ${contractingOfficer.name.split(' ').slice(-1)[0]}` : 'Contracting Officer'},

${companyName} is pleased to submit this comprehensive proposal in response to your solicitation for ${contractData.title}. We have carefully reviewed the requirements and are confident that our proven capabilities, experienced team, and commitment to excellence make us the ideal partner for this important initiative.

## Executive Summary

This proposal demonstrates our company's comprehensive capabilities and commitment to delivering exceptional results for this contract opportunity. Based on our enhanced analysis, we offer:

**Key Value Propositions:**
- **Proven Experience**: ${proposalData.gapAnalysis?.pastPerformanceRelevance?.highlyRelevant?.length || 'Multiple'} highly relevant past performance projects
- **Qualified Team**: ${proposalData.gapAnalysis?.personnelAssessment?.manualPersonnel?.count || 'Experienced'} qualified personnel with ${proposalData.gapAnalysis?.personnelAssessment?.aiExtractedPersonnel?.count || 'additional'} specialized team members
- **Technical Excellence**: ${proposalData.gapAnalysis?.technicalCapabilityAssessment?.core_strengths?.length || 'Strong'} core technical capabilities aligned with your requirements
- **Strategic Advantage**: ${proposalData.gapAnalysis?.winProbability?.current || 75}% win probability based on comprehensive capability alignment

**Our Competitive Differentiators:**
${proposalData.gapAnalysis?.strengthAreas?.map((strength: any, index: number) => 
  `${index + 1}. ${typeof strength === 'string' ? strength : strength.strength || 'Strong capabilities in relevant areas'}`
).join('\n') || `1. Proven track record of successful government contract delivery
2. Comprehensive technical capabilities and innovative solutions
3. Experienced team with relevant security clearances
4. Strong commitment to quality and client satisfaction`}

**Value Proposition:**
We understand that ${contractData.agency} requires a partner who can deliver ${contractData.description ? 'solutions that meet your specific requirements' : 'exceptional results'} while maintaining the highest standards of quality, security, and compliance. Our approach leverages:

- **Deep Domain Expertise**: Extensive experience in ${profileData.industries_served?.join(', ') || 'government contracting'} with a proven track record of success
- **Comprehensive Team**: ${proposalData.gapAnalysis?.personnelAssessment?.overall_team_strength || 'Highly qualified professionals'} ready to execute from day one
- **Technical Innovation**: ${profileData.key_technologies?.join(', ') || 'Advanced technologies'} and methodologies tailored to your specific needs
- **Risk Mitigation**: Comprehensive risk management approach based on ${proposalData.gapAnalysis?.pastPerformanceRelevance?.portfolio_strength || 'extensive experience'} and lessons learned

**Investment and Timeline:**
We propose a cost-effective solution that delivers maximum value within your budget parameters. Our pricing reflects our commitment to providing exceptional service while maintaining competitive rates. We are prepared to begin work immediately upon contract award and will meet all specified deadlines.

**Commitment to Success:**
${companyName} is fully committed to the success of this project. We pledge to bring our full resources, expertise, and dedication to exceed your expectations and deliver results that advance your mission objectives.

We appreciate the opportunity to present our qualifications and look forward to discussing how we can contribute to your continued success. Please contact me at ${companyPhone || '[Phone]'} or ${companyEmail || '[Email]'} if you have any questions or require additional information.

Thank you for your consideration. We are excited about the opportunity to serve ${contractData.agency} and contribute to this important initiative.

Respectfully submitted,

${profileData.ceo_name || profileData.primary_contact || '[Authorized Representative Name]'}
${profileData.ceo_title || 'Chief Executive Officer'}
${companyName}

Enclosures: Technical Proposal, Management Proposal, Past Performance, Personnel Qualifications, Cost Proposal, Compliance Documentation`;
  };

  // 🎯 ENHANCED: Format proposal data for human-readable display with formal cover letter
  const formatEnhancedProposalData = (proposalData: any) => {
    // Format RFP Analysis into readable content
    const formatRFPAnalysis = (rfpData: any) => {
      if (!rfpData) return "RFP analysis not available.";
      
      return `# Contract Analysis

## Contract Overview
**Title:** ${proposalData.contract?.title || contract.title || 'Contract Title Not Available'}
**Agency:** ${proposalData.contract?.agency || contract.agency || 'Agency Not Specified'}
**Solicitation Number:** ${proposalData.contract?.solicitationNumber || contract.solicitationNumber || contract.notice_id || 'Not Specified'}
**Funding Amount:** $${proposalData.contract?.fundingAmount?.toLocaleString() || contract.fundingAmount?.toLocaleString() || 'Not Specified'}
**NAICS Code:** ${proposalData.contract?.naicsCode || contract.naicsCode || 'Not Specified'}
**Due Date:** ${proposalData.contract?.dueDate || contract.dueDate || 'Not Specified'}

## Evaluation Criteria
${rfpData.evaluationCriteria ? Object.entries(rfpData.evaluationCriteria).map(([key, value]: [string, any]) => 
  `### ${key.charAt(0).toUpperCase() + key.slice(1)}
**Weight:** ${value.weight || 'Not specified'}%
**Factors:** ${Array.isArray(value.factors) ? value.factors.join(', ') : 'Not specified'}`
).join('\n\n') : 'Evaluation criteria analysis in progress'}

## Key Requirements
${Array.isArray(rfpData.keyRequirements) ? 
  rfpData.keyRequirements.map((req: string, index: number) => `${index + 1}. ${req}`).join('\n') : 
  'Requirements analysis completed - see detailed breakdown in technical sections'}

## Compliance Requirements
### Federal Acquisition Regulation (FAR) Requirements
- FAR 52.204-10 Reporting Executive Compensation
- FAR 52.225-13 Restrictions on Certain Foreign Purchases
- FAR 52.204-21 Basic Safeguarding of Covered Contractor Information Systems
- FAR 52.219-14 Limitations on Subcontracting

### Defense Federal Acquisition Regulation (DFARS) Requirements
- DFARS 252.204-7012 Safeguarding Covered Defense Information
- DFARS 252.225-7014 Preference for Domestic Specialty Metals
- DFARS 252.204-7018 Prohibition on the Acquisition of Covered Defense Telecommunications

### Security Requirements
${Array.isArray(rfpData.securityRequirements) ? 
  rfpData.securityRequirements.map((req: string) => `- ${req}`).join('\n') : 
  '- Security clearance requirements as specified in contract\n- Facility security requirements if applicable\n- Cybersecurity compliance (NIST, FedRAMP as required)'}

## Contract Complexity Assessment
**Overall Complexity Score:** ${rfpData.complexity?.overallScore || 75}/100
**Technical Complexity:** ${rfpData.complexity?.technicalComplexity || 7}/10
**Management Complexity:** ${rfpData.complexity?.managementComplexity || 6}/10
**Risk Level:** ${rfpData.complexity?.riskLevel || 5}/10
**Estimated Team Size:** ${rfpData.complexity?.estimatedTeamSize || 8} personnel
**Estimated Duration:** ${rfpData.complexity?.estimatedDuration || '12 months'}`;
    };

    // Format Gap Analysis into readable content
    const formatGapAnalysis = (gapData: any) => {
      if (!gapData) return "Profile gap analysis not available.";
      
      return `# Company Profile Analysis

## Company Strengths Assessment
${Array.isArray(gapData.strengthAreas) ? 
  gapData.strengthAreas.map((strength: any, index: number) => 
    `${index + 1}. ${typeof strength === 'string' ? strength : strength.strength || 'Strength identified'}`
  ).join('\n') : 
  `1. Proven capabilities in relevant areas
2. Professional service delivery approach
3. Commitment to quality and compliance
4. Competitive positioning in market`}

## Capability Gap Analysis
${Array.isArray(gapData.capabilityGaps) && gapData.capabilityGaps.length > 0 ? 
  gapData.capabilityGaps.map((gap: any, index: number) => 
    `### ${index + 1}. ${gap.requirement || 'Capability Gap Identified'}
**Current Capability:** ${gap.currentCapability || 'Assessment completed'}
**Gap Severity:** ${gap.gapSeverity || 'Minor'}
**Impact on Proposal:** ${gap.impact || 'Manageable with mitigation strategy'}
**Recommended Mitigation:** ${gap.recommendation || gap.mitigation || 'Mitigation strategy developed'}
**Time to Acquire:** ${gap.timeToAcquire || '3-6 months'}
**Estimated Investment:** ${gap.cost ? '$' + gap.cost.toLocaleString() : 'Minimal'}`
  ).join('\n\n') : 
  'No significant capability gaps identified. Company profile demonstrates strong alignment with contract requirements.'}

## Win Probability Assessment
**Current Win Probability:** ${gapData.winProbability?.current || 65}%
**Potential Win Probability:** ${gapData.winProbability?.potential || 85}%

### Factors Supporting High Win Probability
${Array.isArray(gapData.winProbability?.improvementFactors) ?
  gapData.winProbability.improvementFactors.map((factor: string) => `- ${factor}`).join('\n') :
  `- Strong technical qualifications and relevant experience
- Competitive pricing strategy
- Proven past performance record
- Qualified personnel with appropriate clearances
- Comprehensive understanding of requirements`}

## Strategic Recommendations for Success
${Array.isArray(gapData.recommendations) ?
  gapData.recommendations.map((rec: string, index: number) => `${index + 1}. ${rec}`).join('\n') :
  `1. Emphasize unique technical capabilities and innovations
2. Highlight relevant past performance with similar scope
3. Demonstrate cost-effectiveness and value engineering
4. Showcase qualified personnel and team approach
5. Address all compliance requirements comprehensively`}`;
    };

    // Format Feedback into readable content
    const formatFeedback = (feedbackData: any) => {
      if (!feedbackData) return "AI feedback and recommendations not available.";
      
      return `# AI Proposal Analysis & Recommendations

## Executive Assessment Summary
**Overall Quality Score:** ${feedbackData.overallScore || 78}/100
**Win Probability Estimate:** ${feedbackData.winProbability || proposalData.winProbability?.currentProbability || 68}%
**Confidence Level:** ${feedbackData.confidenceLevel || 'High'}
**Recommendation Tier:** ${feedbackData.recommendationTier || 'Recommended for Submission'}

## Proposal Strengths Identified
${Array.isArray(feedbackData.strengths) ?
  feedbackData.strengths.map((strength: string, index: number) => `${index + 1}. ${strength}`).join('\n') :
  `1. Comprehensive technical approach addressing all requirements
2. Strong competitive positioning with clear value proposition
3. Professional presentation meeting government standards
4. Clear understanding of compliance and regulatory requirements
5. Evidence-based approach with supporting documentation`}

## Strategic Recommendations
${Array.isArray(feedbackData.recommendations) ?
  feedbackData.recommendations.map((rec: string, index: number) => `${index + 1}. ${rec}`).join('\n') :
  `1. Strengthen technical approach with more specific implementation details
2. Enhance value proposition with quantitative benefits
3. Provide more detailed risk assessment and mitigation strategies
4. Include innovation and value-engineering opportunities
5. Ensure all compliance requirements are explicitly addressed`}

## Next Steps for Success
### Immediate Actions (Next 30 Days)
- Complete final proposal review and quality check
- Confirm team availability and commitment letters
- Gather any outstanding compliance documentation
- Prepare presentation materials for potential oral presentation

### Short-Term Enhancements (Next 3-6 Months)
- Address identified capability gaps through training or partnerships
- Strengthen past performance portfolio with detailed documentation
- Pursue relevant industry certifications and clearances
- Enhance competitive intelligence and market positioning

## Final Recommendation
This proposal demonstrates strong qualifications and competitive positioning for the contract opportunity. With the identified enhancements and continued focus on technical excellence, the win probability is estimated at ${feedbackData.winProbability || 68}%. The company should proceed with submission while implementing the recommended improvements for future opportunities.`;
    };

    // Generate formal cover letter
    const formalCoverLetter = generateFormalCoverLetter(proposalData, contract, profile);

    // FIXED: Return properly structured data that matches database expectations
    return {
      contractAnalysis: formatRFPAnalysis(proposalData.rfpAnalysis),
      profileAnalysis: formatGapAnalysis(proposalData.gapAnalysis),
      feedback: formatFeedback(proposalData.feedback),
      coverLetter: formalCoverLetter, // Now includes formal addressing
      technicalSections: [
        {
          title: 'Technical Approach',
          content: formatSectionContent(proposalData.proposalContent?.technicalApproach || proposalData.proposal?.technicalApproach, 'Technical approach')
        },
        {
          title: 'Management Approach',
          content: formatSectionContent(proposalData.proposalContent?.managementApproach || proposalData.proposal?.managementApproach, 'Management approach')
        },
        {
          title: 'Past Performance',
          content: formatSectionContent(proposalData.proposalContent?.pastPerformance || proposalData.proposal?.pastPerformance, 'Past performance')
        },
        {
          title: 'Personnel Qualifications',
          content: formatSectionContent(proposalData.proposalContent?.personnelQualifications || proposalData.proposal?.personnelQualifications, 'Personnel qualifications')
        },
        {
          title: 'Pricing Strategy',
          content: formatSectionContent(proposalData.proposalContent?.pricingStrategy || proposalData.proposal?.pricing, 'Pricing strategy')
        },
        {
          title: 'Compliance & Certifications',
          content: formatSectionContent(proposalData.proposalContent?.complianceSection || proposalData.proposal?.compliance, 'Compliance and certifications')
        }
      ]
    };
  };

  // Helper function to safely format section content
  const formatSectionContent = (content: any, sectionType: string) => {
    if (typeof content === 'string' && content.trim().length > 0) {
      return content;
    }
    
    if (typeof content === 'object' && content !== null) {
      const values = Object.values(content).filter(v => typeof v === 'string' && v.trim().length > 0);
      if (values.length > 0) {
        return values.join('\n\n');
      }
    }
    
    return `${sectionType.charAt(0).toUpperCase() + sectionType.slice(1)} content will be developed based on your company profile, contract requirements, and industry best practices.`;
  };

  const updateStepStatus = (stepId: string, status: StepStatus['status']) => {
    setSteps(prevSteps => 
      prevSteps.map(step => 
        step.id === stepId ? { ...step, status } : step
      )
    );
  };

  const updateProgress = (stepId: string) => {
    const stepIndex = ENHANCED_GENERATION_STEPS.findIndex(s => s.id === stepId);
    const newProgress = ((stepIndex + 1) / ENHANCED_GENERATION_STEPS.length) * 100;
    setProgress(newProgress);
    setCurrentStep(stepId);
  };

  // Check if proposal already exists
  const checkExistingProposal = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const noticeId = contract.notice_id || contract.id;
      if (!noticeId) {
        setHasStartedGeneration(false); // Allow generation to start
        handleComprehensiveGeneration();
        return;
      }

      // Check for existing enhanced proposal
      const { data: existing, error } = await supabase
        .from('proposal_templates')
        .select('*')
        .eq('profile_id', user.id)
        .eq('notice_id', noticeId)
        .eq('proposal_type', 'enhanced')
        .maybeSingle();

      if (error) {
        console.error('Error checking existing proposal:', error);
        setHasStartedGeneration(false); // Allow generation to start
        handleComprehensiveGeneration();
        return;
      }

      if (existing) {
        console.log('📋 Found existing enhanced proposal:', existing);
        setExistingProposal(existing);
        setShowOverwriteConfirm(true);
      } else {
        console.log('📝 No existing enhanced proposal found, proceeding with generation');
        setHasStartedGeneration(false); // Allow generation to start
        handleComprehensiveGeneration();
      }
    } catch (error) {
      console.error('Error in checkExistingProposal:', error);
      setHasStartedGeneration(false); // Allow generation to start
      handleComprehensiveGeneration(); // Proceed anyway
    }
  };

  // Handle overwrite confirmation
  const handleOverwriteConfirm = (shouldOverwrite: boolean) => {
    setShowOverwriteConfirm(false);
    if (shouldOverwrite) {
      setHasStartedGeneration(false); // Reset the flag
      handleComprehensiveGeneration(true); // Pass overwrite flag
    } else {
      // User chose not to overwrite, treat as success and show existing proposal
      setProposalContent({
        success: true,
        isExisting: true,
        metadata: {
          contractTitle: contract.title,
          agency: contract.agency,
          generatedAt: existingProposal.created_at
        }
      });
      toast.success('Using existing enhanced proposal');
    }
  };

  // Validate that contract exists in sam_contracts table
  const validateContractExists = async (noticeId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('sam_contracts')
        .select('notice_id')
        .eq('notice_id', noticeId)
        .single();

      if (error || !data) {
        console.log(`⚠️ Contract ${noticeId} not found in sam_contracts table`);
        return false;
      }

      console.log(`✅ Contract ${noticeId} exists in sam_contracts table`);
      return true;
    } catch (error) {
      console.error('Error validating contract:', error);
      return false;
    }
  };

  // Create temporary contract if needed
  const ensureContractExists = async (contract: Contract): Promise<string> => {
    const noticeId = contract.notice_id || contract.id || `TEMP_${Date.now()}`;
    
    // First check if contract exists
    const exists = await validateContractExists(noticeId);
    if (exists) {
      return noticeId;
    }

    // If contract doesn't exist, create a temporary one
    console.log('📝 Creating temporary contract entry for proposal...');
    
    try {
      const tempNoticeId = `TEMP_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const { error: insertError } = await supabase
        .from('sam_contracts')
        .insert({
          notice_id: tempNoticeId,
          title: contract.title || 'Enhanced Proposal Contract',
          agency: contract.agency || 'Unknown Agency',
          naics_code: contract.naicsCode || '541511',
          posted_date: new Date().toISOString(),
          due_date: contract.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          description: contract.description || 'Temporary contract created for enhanced proposal generation',
          status: 'open',
          active: false, // Mark as inactive since it's temporary
          funding_amount: contract.fundingAmount || 0,
          set_asides: contract.setAsides || []
        });

      if (insertError) {
        console.error('Error creating temporary contract:', insertError);
        throw new Error('Failed to create temporary contract');
      }

      console.log(`✅ Created temporary contract: ${tempNoticeId}`);
      return tempNoticeId;
      
    } catch (error) {
      console.error('Error in ensureContractExists:', error);
      // Fallback: use original ID and let the trigger handle it
      return noticeId;
    }
  };

  const handleComprehensiveGeneration = async (overwrite: boolean = false) => {
    // Prevent duplicate generation
    if (hasStartedGeneration && !overwrite) {
      console.log('Generation already started, skipping...');
      return;
    }

    try {
      setHasStartedGeneration(true);
      setGenerating(true);
      setCancelling(false);
      setError(null);
      setProgress(0);

      if (!profile) {
        throw new Error('Please complete your profile before generating a proposal');
      }

      // Validate that we have essential contract data
      if (!contract.id && !contract.notice_id) {
        throw new Error('Contract missing required ID fields');
      }

      if (!contract.title || contract.title === 'undefined') {
        throw new Error('Contract missing required title');
      }

      // Reset all steps
      setSteps(ENHANCED_GENERATION_STEPS.map(step => ({ ...step, status: 'pending' })));

      // Ensure contract exists in database before proceeding
      const validatedNoticeId = await ensureContractExists(contract);
      const contractId = contract.id || validatedNoticeId;
      
      console.log('📝 Using validated IDs:', { contractId, validatedNoticeId });

      // Fetch extracted requirements from attached documents (if any)
      let extractedRequirements = null;
      let extractedSOW = null;
      let extractedEvaluation = null;
      let documentText = '';

      try {
        // Fetch requirements from contract_requirements table
        const { data: reqData } = await supabase
          .from('contract_requirements')
          .select('*')
          .eq('contract_id', validatedNoticeId);

        if (reqData && reqData.length > 0) {
          extractedRequirements = reqData;
          console.log(`📋 Found ${reqData.length} extracted requirements from documents`);
        }

        // Fetch SOW data
        const { data: sowData } = await supabase
          .from('contract_sow')
          .select('*')
          .eq('contract_id', validatedNoticeId)
          .single();

        if (sowData) {
          extractedSOW = sowData;
          console.log('📄 Found extracted Statement of Work');
        }

        // Fetch evaluation criteria
        const { data: evalData } = await supabase
          .from('contract_evaluation_criteria')
          .select('*')
          .eq('contract_id', validatedNoticeId)
          .single();

        if (evalData) {
          extractedEvaluation = evalData;
          console.log('📊 Found extracted evaluation criteria');
        }

        // Fetch document text for additional context
        console.log(`🔍 Fetching documents for contract_id: ${validatedNoticeId}`);

        const { data: docData, error: docFetchError } = await supabase
          .from('contract_documents')
          .select('id, document_name, raw_text, status, document_url')
          .eq('contract_id', validatedNoticeId);

        console.log(`📂 Found ${docData?.length || 0} document records:`, docData?.map(d => ({
          id: d.id,
          name: d.document_name,
          status: d.status,
          hasRawText: !!d.raw_text,
          rawTextLength: d.raw_text?.length || 0
        })));

        if (docFetchError) {
          console.error('❌ Error fetching documents:', docFetchError);
        }

        // Filter to only completed documents with text
        const completedDocs = (docData || []).filter(d => d.status === 'completed' && d.raw_text);

        if (completedDocs.length > 0) {
          documentText = completedDocs.map(d => `=== ${d.document_name} ===\n${d.raw_text}`).join('\n\n');
          console.log(`✅ Found ${completedDocs.length} documents with extracted text (${documentText.length} chars)`);
        } else {
          console.warn(`⚠️ No completed documents with extracted text found for ${validatedNoticeId}`);
          console.log('Document statuses:', docData?.map(d => `${d.document_name}: ${d.status}`) || 'none');
        }
      } catch (docError) {
        console.warn('⚠️ Could not fetch extracted requirements (non-blocking):', docError);
      }

      const cleanContract = {
        id: contractId,
        notice_id: validatedNoticeId,
        title: contract.title || 'Untitled Contract',
        description: contract.description || 'No description available',
        agency: contract.agency || 'Unknown Agency',
        naicsCode: contract.naicsCode || '',
        naicsCodes: contract.naicsCodes || [],
        dueDate: contract.dueDate || new Date().toISOString(),
        fundingAmount: contract.fundingAmount || 0,
        setAsides: contract.setAsides || [],
        status: contract.status || 'open',
        type: contract.type || '',
        solicitationNumber: contract.solicitationNumber,
        placeOfPerformance: contract.placeOfPerformance ? {
          city: contract.placeOfPerformance.city || '',
          state: contract.placeOfPerformance.state || '',
          zip: contract.placeOfPerformance.zip || ''
        } : null,
        evaluationCriteria: contract.evaluationCriteria || extractedEvaluation || null,
        requiredCertifications: contract.requiredCertifications || [],
        // Include extracted data from attached documents
        extractedRequirements,
        extractedSOW,
        extractedEvaluation,
        documentText: documentText.slice(0, 100000) // Limit to 100KB
      };

      // 🚀 STEP 1: COMPREHENSIVE INTELLIGENCE EXTRACTION
      updateStepStatus('intelligence-extraction', 'active');
      updateProgress('intelligence-extraction');
      
      if (cancelling) return; // Check for cancellation
      
      console.log('🧠 Starting comprehensive intelligence extraction...');
      const intelligentResults = await IntelligentProposalGenerator.generateIntelligentProposal(
        profile.id,
        cleanContract
      );

      if (cancelling) return; // Check for cancellation

      console.log('📊 Intelligence extraction completed:', intelligentResults.dataQualityReport);
      setAnalysisData(prev => ({ ...prev, intelligenceData: intelligentResults }));
      updateStepStatus('intelligence-extraction', 'complete');

      // Step 2: RFP Analysis
      updateStepStatus('rfp-analysis', 'active');
      updateProgress('rfp-analysis');
      
      if (cancelling) return; // Check for cancellation
      
      const { data: rfpData, error: rfpError } = await supabase.functions.invoke('analyze-rfp', {
        body: JSON.stringify({ contract: cleanContract })
      });

      if (cancelling) return; // Check for cancellation

      if (rfpError) {
        throw new Error(rfpError.message || 'Failed to analyze RFP');
      }

      setAnalysisData(prev => ({ ...prev, rfpAnalysis: rfpData.analysis }));
      updateStepStatus('rfp-analysis', 'complete');

      // Step 3: Enhanced Gap Analysis with Comprehensive Intelligence
      updateStepStatus('gap-analysis', 'active');
      updateProgress('gap-analysis');
      
      if (cancelling) return; // Check for cancellation
      
      const { data: gapData, error: gapError } = await supabase.functions.invoke('enhance-profile', {
        body: JSON.stringify({ 
          intelligentContext: intelligentResults.intelligentContext,
          proposalInstructions: intelligentResults.proposalInstructions,
          contract: cleanContract,
          rfpAnalysis: rfpData.analysis,
          dataQualityReport: intelligentResults.dataQualityReport
        })
      });

      if (cancelling) return; // Check for cancellation

      if (gapError) {
        console.warn('Gap analysis encountered issues, using fallback:', gapError);
        // The function now returns fallback data instead of failing
      }

      setAnalysisData(prev => ({ ...prev, gapAnalysis: gapData?.gapAnalysis || {} }));
      updateStepStatus('gap-analysis', 'complete');

      // Step 4: Generate Deliverables (BEFORE proposal generation so pricing can use labor data)
      updateStepStatus('deliverables-generation', 'active');
      updateProgress('deliverables-generation');

      if (cancelling) return;

      let deliverablesData = null;
      try {
        console.log('📦 Generating deliverables analysis...');

        // Step 4a: Extract deliverables from RFQ
        const { data: extractedDeliverables, error: extractError } = await supabase.functions.invoke('extract-deliverables-raw', {
          body: {
            rfq_text: cleanContract.documentText || cleanContract.description || '',
            rfq_attachments: [],
            contract_metadata: {
              notice_id: validatedNoticeId,
              title: cleanContract.title,
              contract_type: cleanContract.type || 'Unknown',
              naics_code: cleanContract.naicsCode || '',
              agency: cleanContract.agency,
              estimated_value: cleanContract.fundingAmount || 0
            }
          }
        });

        if (extractError) {
          console.warn('⚠️ Deliverables extraction warning:', extractError);
        }

        if (cancelling) return;

        // Step 4b: Enrich with compliance requirements
        let enrichedDeliverables = extractedDeliverables;
        if (extractedDeliverables?.deliverables?.length > 0) {
          const { data: enriched, error: enrichError } = await supabase.functions.invoke('enrich-deliverables-compliance', {
            body: {
              extracted_deliverables: extractedDeliverables,
              contract_metadata: {
                notice_id: validatedNoticeId,
                title: cleanContract.title,
                contract_type: cleanContract.type || 'Unknown',
                naics_code: cleanContract.naicsCode || '',
                agency: cleanContract.agency,
                estimated_value: cleanContract.fundingAmount || 0
              },
              far_clauses: rfpData?.analysis?.parsedSolicitation?.sectionI?.farClauses || [],
              agency_requirements: rfpData?.analysis?.contractCharacteristics?.agencySpecific || null
            }
          });

          if (!enrichError && enriched) {
            enrichedDeliverables = enriched;
          }
        }

        if (cancelling) return;

        // Step 4c: Generate labor framework
        if (enrichedDeliverables?.deliverables?.length > 0) {
          const { data: laborFramework, error: laborError } = await supabase.functions.invoke('generate-labor-framework', {
            body: {
              enriched_deliverables: enrichedDeliverables,
              user_profile: {
                labor_categories: profile?.pricing_data?.labor_categories,
                past_performance: profile?.past_performance
              },
              contract_constraints: {
                hours_ceiling: cleanContract.fundingAmount ? Math.round(cleanContract.fundingAmount / 100) : null,
                budget_ceiling: cleanContract.fundingAmount || null,
                period_months: 12 // Default to 12 months if not specified
              }
            }
          });

          if (!laborError && laborFramework) {
            deliverablesData = {
              deliverables_with_labor: laborFramework.deliverables_with_labor || [],
              labor_summary: laborFramework.labor_summary || { by_category: [], total_hours: { min: 0, max: 0, recommended: 0 } },
              pricing_inputs: laborFramework.pricing_inputs || { labor_categories_required: [], hours_matrix: {}, suggested_rate_basis: 'market', sca_wage_determination_needed: false },
              compliance_framework: laborFramework.compliance_framework || enrichedDeliverables.compliance_framework,
              timeline_summary: extractedDeliverables?.timeline_summary,
              volume_summary: extractedDeliverables?.volume_summary
            };
            console.log(`✅ Generated ${deliverablesData.deliverables_with_labor.length} deliverables with labor framework`);
          }
        }
      } catch (deliverableError) {
        console.warn('⚠️ Deliverables generation warning (non-blocking):', deliverableError);
        // Deliverables generation is optional, continue without it
      }

      updateStepStatus('deliverables-generation', 'complete');

      // Step 5: Generate Comprehensive Proposal with Full Intelligence (now includes deliverables data for pricing)
      updateStepStatus('proposal-generation', 'active');
      updateProgress('proposal-generation');

      if (cancelling) return; // Check for cancellation

      const { data: proposalData, error: proposalError } = await supabase.functions.invoke('generate-complete-proposal', {
        body: JSON.stringify({
          intelligentContext: intelligentResults.intelligentContext,
          proposalInstructions: intelligentResults.proposalInstructions,
          contract: cleanContract,
          rfpAnalysis: rfpData.analysis,
          gapAnalysis: gapData?.gapAnalysis,
          dataQualityReport: intelligentResults.dataQualityReport,
          // Pass deliverables data for pricing generation
          deliverablesData: deliverablesData ? {
            pricing_inputs: deliverablesData.pricing_inputs,
            labor_summary: deliverablesData.labor_summary,
            deliverables_count: deliverablesData.deliverables_with_labor?.length || 0,
            compliance_framework: deliverablesData.compliance_framework
          } : null
        })
      });

      if (cancelling) return; // Check for cancellation

      if (proposalError) {
        throw new Error(proposalError.message || 'Failed to generate proposal');
      }

      updateStepStatus('proposal-generation', 'complete');

      // Step 6: Finalization - Store Comprehensive Proposal
      updateStepStatus('finalization', 'active');
      updateProgress('finalization');

      if (cancelling) return; // Check for cancellation

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }
      
      // Create enhanced feedback data
      const enhancedFeedback = {
        overallScore: 78,
        winProbability: gapData?.gapAnalysis?.winProbability?.current || 68,
        confidenceLevel: 'High',
        strengths: [
          'Comprehensive technical approach addressing all requirements',
          'Strong competitive positioning with clear value proposition',
          'Professional presentation meeting government standards',
          'Clear understanding of compliance and regulatory requirements',
          'Evidence-based approach with supporting documentation'
        ],
        recommendations: [
          'Strengthen technical approach with more specific implementation details',
          'Enhance value proposition with quantitative benefits',
          'Provide more detailed risk assessment and mitigation strategies',
          'Include innovation and value-engineering opportunities',
          'Ensure all compliance requirements are explicitly addressed'
        ]
      };

      // Combine all data for formatting
      const completeProposalData = {
        proposalContent: proposalData.proposalContent || proposalData,
        rfpAnalysis: rfpData.analysis,
        gapAnalysis: gapData?.gapAnalysis || {},
        feedback: enhancedFeedback,
        contract: cleanContract,
        profile: profile
      };

      // FIXED: Use the local formatter function instead of the broken class
      const formattedData = formatEnhancedProposalData(completeProposalData);

      // IMPORTANT: Add aiFeedback to content_json so it's always available
      // This ensures feedback shows even if enhanced_data column doesn't exist
      formattedData.aiFeedback = enhancedFeedback;
      formattedData.gapAnalysis = gapData?.gapAnalysis || {};
      formattedData.winProbability = gapData?.gapAnalysis?.winProbability || { currentProbability: 68 };
      formattedData.proposalType = 'orchestrated'; // Mark for proper tab detection

      // Add deliverables data if generated
      if (deliverablesData) {
        formattedData.deliverablesData = deliverablesData;
      }

      // Validate the formatted data structure
      if (!formattedData.contractAnalysis || !formattedData.profileAnalysis ||
          !formattedData.coverLetter || !Array.isArray(formattedData.technicalSections)) {
        throw new Error('Invalid proposal data structure after formatting');
      }

      console.log('📝 Storing comprehensive proposal with formal cover letter...');
      
      // Enhanced storage with comprehensive intelligence metadata
      const proposalRecord = {
        profile_id: user.id,
        notice_id: validatedNoticeId,
        contract_id: contractId,
        proposal_type: 'enhanced',
        template_name: `Enhanced Proposal for ${cleanContract.title}`,
        content_json: formattedData,
        enhanced_data: {
          proposal: proposalData.proposalContent || proposalData,
          rfpAnalysis: rfpData.analysis,
          gapAnalysis: gapData?.gapAnalysis || {},
          feedback: enhancedFeedback,
          intelligentContext: intelligentResults.intelligentContext,
          dataQualityReport: intelligentResults.dataQualityReport,
          winProbability: gapData?.gapAnalysis?.winProbability || { currentProbability: 68 },
          deliverablesData: deliverablesData || null,
          metadata: {
            generatedAt: new Date().toISOString(),
            profileDataQuality: intelligentResults.dataQualityReport?.overall_assessment || 'good',
            detectedIndustry: intelligentResults.intelligentContext?.contract_intelligence?.detected_industry,
            dataSourcesUsed: intelligentResults.dataQualityReport?.confidence_indicators?.high_quality_data_sources || [],
            intelligenceScore: intelligentResults.intelligentContext?.generation_context?.data_quality_score || 0,
            comprehensiveFeatures: {
              aiPersonnelExtracted: intelligentResults.dataQualityReport?.intelligence_summary?.ai_personnel_found || 0,
              documentsAnalyzed: intelligentResults.dataQualityReport?.data_completeness?.documents || 0,
              pastPerformanceProjects: intelligentResults.dataQualityReport?.data_completeness?.past_performance || 0,
              pricingComponentsUsed: intelligentResults.dataQualityReport?.data_completeness?.pricing_components || 0,
              capabilitiesFound: intelligentResults.dataQualityReport?.intelligence_summary?.technical_capabilities_found || 0,
              formalCoverLetter: true,
              deliverablesGenerated: deliverablesData?.deliverables_with_labor?.length || 0
            }
          }
        },
        template_url: `enhanced-proposals/${validatedNoticeId}/${Date.now()}/complete-proposal.pdf`,
        updated_at: new Date().toISOString()
      };

      const { error: storageError } = await supabase
        .from('proposal_templates')
        .upsert(proposalRecord, {
          onConflict: 'profile_id,notice_id'
        });

      if (storageError) {
        console.error('Storage error details:', storageError);
        throw new Error('Failed to save proposal: ' + storageError.message);
      }

      updateStepStatus('finalization', 'complete');
      updateProgress('finalization');
      
      setProposalContent({
        success: true,
        winProbability: gapData?.gapAnalysis?.winProbability || { currentProbability: 68 },
        metadata: {
          contractTitle: cleanContract.title,
          agency: cleanContract.agency,
          generatedAt: new Date().toISOString(),
          profileDataQuality: intelligentResults.dataQualityReport?.overall_assessment || 'good',
          intelligenceScore: intelligentResults.intelligentContext?.generation_context?.data_quality_score || 0,
          comprehensiveFeatures: proposalRecord.enhanced_data.metadata.comprehensiveFeatures
        }
      });

      console.log('✅ Comprehensive proposal generation completed successfully!');
      console.log('📊 Intelligence Summary:', intelligentResults.dataQualityReport);
      console.log('🎯 Features Used:', proposalRecord.enhanced_data.metadata.comprehensiveFeatures);
      
      toast.success('Comprehensive enhanced proposal generated successfully!');
      onSuccess();
      
    } catch (error) {
      console.error('Error generating comprehensive proposal:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate comprehensive proposal';
      setError(errorMessage);
      toast.error(errorMessage);
      
      setSteps(prevSteps => 
        prevSteps.map(step => 
          step.id === currentStep ? { ...step, status: 'error' } : step
        )
      );
    } finally {
      setGenerating(false);
      // Don't reset hasStartedGeneration here to prevent duplicate runs
    }
  };

  const getStepIcon = (status: StepStatus['status']) => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="h-6 w-6 text-success" />;
      case 'active':
        return <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>;
      case 'error':
        return <AlertCircle className="h-6 w-6 text-urgent" />;
      default:
        return <Clock className="h-6 w-6 text-bw-muted" />;
    }
  };

  // Overwrite confirmation modal
  if (showOverwriteConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-bw-surface rounded-lg shadow-xl w-full max-w-md">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-fit-moderate mr-3" />
              <h3 className="text-lg font-semibold text-bw-ink">Enhanced Proposal Exists</h3>
            </div>
            <p className="text-bw-muted mb-6">
              An enhanced proposal already exists for this contract. Would you like to:
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => handleOverwriteConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-bw-ink rounded-md hover:bg-gray-300 transition-colors"
              >
                Use Existing
              </button>
              <button
                onClick={() => handleOverwriteConfirm(true)}
                className="flex-1 px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Generate New
              </button>
            </div>
            <div className="mt-4 text-center">
              <button
                onClick={onClose}
                className="text-sm text-bw-muted hover:text-bw-ink"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-bw-surface rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-bw-surface border-b px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-bw-ink">Comprehensive Proposal Generation</h2>
              <p className="text-bw-muted">{contract.title}</p>
              <p className="text-sm text-bw-primary">🧠 Comprehensive Intelligence + Formal Cover Letter</p>
            </div>
            <button
              onClick={handleCancel}
              className="p-2 rounded-full text-bw-muted hover:text-bw-muted disabled:opacity-50"
              disabled={cancelling}
              title={generating ? "Cancel generation" : "Close"}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-bw-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-bw-muted mt-2">{Math.round(progress)}% Complete</p>
          </div>
        </div>

        <div className="p-6">
          {/* Steps */}
          <div className="space-y-4 mb-8">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`flex items-center p-4 rounded-lg border ${
                  step.status === 'active' ? 'border-blue-500 bg-bw-primary-tint' :
                  step.status === 'complete' ? 'border-green-500 bg-success-bg' :
                  step.status === 'error' ? 'border-red-500 bg-urgent-bg' :
                  'border-bw-border bg-bw-surface-subtle'
                }`}
              >
                <div className="flex-shrink-0 mr-4">
                  {getStepIcon(step.status)}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-bw-ink">{step.label}</h3>
                  <p className="text-bw-muted">{step.description}</p>
                  {step.id === 'intelligence-extraction' && step.status === 'active' && (
                    <p className="text-sm text-bw-primary mt-1">🧠 Extracting comprehensive intelligence from all profile data...</p>
                  )}
                  {step.id === 'rfp-analysis' && step.status === 'active' && (
                    <p className="text-sm text-bw-primary mt-1">🔍 Analyzing contract requirements and compliance needs...</p>
                  )}
                  {step.id === 'gap-analysis' && step.status === 'active' && (
                    <p className="text-sm text-bw-primary mt-1">📊 Using comprehensive intelligence for gap analysis...</p>
                  )}
                  {step.id === 'proposal-generation' && step.status === 'active' && (
                    <p className="text-sm text-bw-primary mt-1">📝 Generating comprehensive proposal with formal cover letter...</p>
                  )}
                </div>
                <div className="text-sm text-bw-muted">
                  Step {index + 1} of {steps.length}
                </div>
              </div>
            ))}
          </div>

          {/* Intelligence Summary (shown after extraction) */}
          {analysisData.intelligenceData && (
            <div className="mb-6 p-4 bg-success-bg border border-success-border rounded-lg">
              <h4 className="font-medium text-green-900 mb-2">Comprehensive Intelligence Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-success">Data Quality:</span>
                  <span className="ml-2 font-medium">{analysisData.intelligenceData.dataQualityReport?.overall_assessment || 'Good'}</span>
                </div>
                <div>
                  <span className="text-success">Intelligence Score:</span>
                  <span className="ml-2 font-medium">{analysisData.intelligenceData.intelligentContext?.generation_context?.data_quality_score || 85}/100</span>
                </div>
                <div>
                  <span className="text-success">Documents Analyzed:</span>
                  <span className="ml-2 font-medium">{analysisData.intelligenceData.dataQualityReport?.data_completeness?.documents || 0}</span>
                </div>
                <div>
                  <span className="text-success">AI Personnel Found:</span>
                  <span className="ml-2 font-medium">{analysisData.intelligenceData.dataQualityReport?.intelligence_summary?.ai_personnel_found || 0}</span>
                </div>
                <div>
                  <span className="text-success">Technical Capabilities:</span>
                  <span className="ml-2 font-medium">{analysisData.intelligenceData.dataQualityReport?.intelligence_summary?.technical_capabilities_found || 0}</span>
                </div>
                <div>
                  <span className="text-success">Industry Detected:</span>
                  <span className="ml-2 font-medium">{analysisData.intelligenceData.intelligentContext?.contract_intelligence?.detected_industry || 'General'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-urgent-bg border border-urgent-border rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-urgent mr-2" />
                <span className="text-urgent font-medium">Error:</span>
              </div>
              <p className="text-urgent mt-2">{error}</p>
            </div>
          )}

          {/* Success Display */}
          {proposalContent && proposalContent.success && (
            <div className="bg-success-bg border border-success-border rounded-lg p-6">
              <div className="flex items-center mb-4">
                <CheckCircle className="h-6 w-6 text-success mr-2" />
                <h3 className="text-lg font-semibold text-green-900">
                  {proposalContent.isExisting ? 'Using Existing Enhanced Proposal!' : 'Comprehensive Enhanced Proposal Generated!'}
                </h3>
              </div>
              <div className="space-y-2">
                <p className="text-success">
                  {proposalContent.isExisting 
                    ? 'Using your existing enhanced proposal for this contract.'
                    : 'Your comprehensive government proposal has been generated with full intelligence analysis, formal cover letter, and professional formatting!'
                  }
                </p>
                <p className="text-sm text-success">
                  <strong>Contract:</strong> {proposalContent.metadata?.contractTitle}
                </p>
                <p className="text-sm text-success">
                  <strong>Agency:</strong> {proposalContent.metadata?.agency}
                </p>
                <p className="text-sm text-success">
                  <strong>{proposalContent.isExisting ? 'Created' : 'Generated'}:</strong> {new Date(proposalContent.metadata?.generatedAt).toLocaleString()}
                </p>
                <p className="text-sm text-success">
                  <strong>Win Probability:</strong> {proposalContent.winProbability?.currentProbability || 68}%
                </p>
                <p className="text-sm text-success">
                  <strong>Intelligence Score:</strong> {proposalContent.metadata?.intelligenceScore || 85}/100
                </p>
                <p className="text-sm text-success">
                  <strong>Data Quality:</strong> {proposalContent.metadata?.profileDataQuality || 'Good'}
                </p>
                {proposalContent.metadata?.comprehensiveFeatures && (
                  <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                    <div>✅ AI Personnel: {proposalContent.metadata.comprehensiveFeatures.aiPersonnelExtracted}</div>
                    <div>✅ Documents: {proposalContent.metadata.comprehensiveFeatures.documentsAnalyzed}</div>
                    <div>✅ Projects: {proposalContent.metadata.comprehensiveFeatures.pastPerformanceProjects}</div>
                    <div>✅ Capabilities: {proposalContent.metadata.comprehensiveFeatures.capabilitiesFound}</div>
                    <div>✅ Pricing: {proposalContent.metadata.comprehensiveFeatures.pricingComponentsUsed}</div>
                    <div>✅ Formal Cover Letter: {proposalContent.metadata.comprehensiveFeatures.formalCoverLetter ? 'Yes' : 'No'}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between space-x-4 pt-6 border-t">
            {/* Cancel/Close Button */}
            <button
              onClick={handleCancel}
              className={`px-6 py-2 rounded-md transition-colors ${
                generating 
                  ? 'bg-urgent text-white hover:bg-red-700' 
                  : 'bg-gray-300 text-bw-ink hover:bg-gray-400'
              }`}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelling...' : generating ? 'Cancel Generation' : 'Close'}
            </button>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              {!generating && !error && proposalContent?.success && (
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  View Comprehensive Proposal
                </button>
              )}
              {error && !cancelling && (
                <button
                  onClick={() => {
                    setError(null);
                    setHasStartedGeneration(false);
                    handleComprehensiveGeneration();
                  }}
                  className="px-6 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Retry Generation
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}